import type { ReactNode } from 'react';
import { ShieldAlert } from 'lucide-react';
import { Skeleton } from '@/shared/components/feedback/Skeleton';
import { Button } from '@/shared/components/ui/Button';

export type WidgetSkeletonVariant = 'kpi' | 'list' | 'table' | 'chart';

const SKELETON_TONE = 'bg-surface-container';

/**
 * Loading is a shape of the thing that is coming, not a spinner over an empty
 * box. The region carries aria-busy so a screen reader knows it is filling in.
 */
export function WidgetLoading({ variant = 'kpi' }: { variant?: WidgetSkeletonVariant }) {
  return (
    <div
      className="h-full w-full"
      role="status"
      aria-busy="true"
      aria-live="polite"
    >
      <span className="sr-only">Loading</span>
      {variant === 'kpi' && (
        <div className="grid grid-cols-2 gap-4">
          {[0, 1].map((i) => (
            <div key={i} className="flex flex-col gap-2.5">
              <Skeleton className={`h-3 w-2/3 rounded ${SKELETON_TONE}`} />
              <Skeleton className={`h-5 w-1/2 rounded ${SKELETON_TONE}`} />
            </div>
          ))}
        </div>
      )}
      {variant === 'list' && (
        <div className="flex flex-col gap-2.5">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className={`h-8 w-8 shrink-0 rounded-card-sm ${SKELETON_TONE}`} />
              <div className="flex-1 space-y-1.5">
                <Skeleton className={`h-3 w-2/3 rounded ${SKELETON_TONE}`} />
                <Skeleton className={`h-2.5 w-1/3 rounded ${SKELETON_TONE}`} />
              </div>
            </div>
          ))}
        </div>
      )}
      {variant === 'table' && (
        <div className="space-y-2">
          <Skeleton className={`h-7 w-full rounded ${SKELETON_TONE}`} />
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className={`h-5 w-full rounded ${SKELETON_TONE}`} />
          ))}
        </div>
      )}
      {variant === 'chart' && (
        <div className="flex h-[180px] items-end gap-2">
          {['h-10', 'h-16', 'h-12', 'h-20', 'h-14', 'h-24', 'h-16', 'h-12'].map((h, i) => (
            <Skeleton
              key={i}
              className={`w-full rounded-t ${h} ${SKELETON_TONE}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Empty says what is missing AND what to do about it. It never renders a zero,
 * a dash-row or a sample record to fill the space.
 */
export function WidgetEmpty({ message = 'Nothing recorded for this yet', action }: { message?: string; action?: ReactNode }) {
  return (
    <div className="flex h-full flex-col items-start justify-center gap-2.5 py-2">
      <p className="text-body-sm text-on-surface-variant">{message}</p>
      {action}
    </div>
  );
}

// Widget data hooks pass `error?.message` straight through, which for a failed
// HTTP call is axios's own text ("Request failed with status code 403") — a
// leaky implementation detail, not something a user should read. Translate the
// common cases; anything already written as a normal sentence passes through
// unchanged, so a deliberately-thrown human-readable error still shows as-is.
function humanizeWidgetError(message?: string): string {
  if (!message) return 'Failed to load widget.';
  const statusMatch = message.match(/status code (\d+)/i);
  if (statusMatch) {
    const code = Number(statusMatch[1]);
    if (code === 401) return 'Your session has expired — please sign in again.';
    if (code === 403) return "You don't have permission to view this.";
    if (code === 404) return 'This data is not available.';
    if (code >= 500) return 'Something went wrong on our end. Please try again.';
    return 'Could not load this widget.';
  }
  if (/network error/i.test(message)) return "Can't reach the server — check your connection.";
  return message;
}

export function WidgetError({ message = 'Failed to load widget', onRetry }: { message?: string; onRetry?: () => void }) {
  return (
    <div className="flex h-full flex-col items-start gap-2">
      <p className="text-body-sm text-on-surface-variant">{humanizeWidgetError(message)}</p>
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry}>Retry</Button>
      )}
    </div>
  );
}

/** Permission-denied is its own fact — distinct from "nothing to show" and
 *  never rendered as an empty table or a zero. */
export function WidgetPermissionDenied({ title = 'Not on your access', detail }: { title?: string; detail?: string }) {
  return (
    <div className="flex h-full flex-col items-start gap-1.5 py-1">
      <div className="flex items-center gap-2 text-body-sm font-semibold text-on-surface">
        <ShieldAlert className="h-4 w-4 shrink-0 text-on-surface-subtle" aria-hidden />
        {title}
      </div>
      <p className="text-body-sm text-on-surface-variant">
        {detail || 'Your role cannot read this data. Ask your department head to grant the workspace.'}
      </p>
    </div>
  );
}
